/**
 * The gateway panel's multi-protocol instrument forms (web brief U6), for a
 * gateway that accepts config schema v2:
 *
 * - {@link InstrumentFormV2}: add an instrument with the v2 body. Pick the
 *   protocol (drivers this gateway may use), fill its connection from the
 *   driver's link_schema (SchemaForm; serial ports from the birth), and its
 *   points from the address_schema (PointAddressEditor). A Modbus instrument
 *   can start from a VSync equipment template. A 422 `fields[]` lands on the
 *   connection field or the point it names.
 * - {@link InstrumentLinkEditor}: edit an existing v2 instrument's name, poll
 *   rate and connection with PATCH. Secrets are write-only: a stored password
 *   is kept unless a new one is typed, or cleared on request. Points are not
 *   sent, so they (and their tags, bindings and history) stay as they are. A
 *   poll rate typed here sets EVERY point's (the body's `poll_sec` is a bulk
 *   set), so it is labelled as such; one point's is edited in InstrumentValues.
 *
 * Gateways without config schema v2 keep the v1 Modbus form in
 * GatewayInstruments, unchanged.
 */

import { useId, useMemo, useState, type ReactNode } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import {
  fieldProblemsOf,
  toFormFieldErrors,
  things,
  writeErrorMessage,
  type CreatedInstrument,
  type DriverInfo,
  type EquipmentTemplate,
  type FormFieldErrors,
  type InstrumentPatched,
  type Thing,
} from "@/lib/api";
import { driverLabel, isModbusDriver, linkAdvancedFields } from "@/lib/iot/drivers";
import { formPointsToV2, templateToFormPoints, type FormPoint } from "@/lib/iot/instrument-body";
import { isPollSec } from "@/lib/iot/instrument-points";
import { formDefaults, formToParams, paramsToForm, validateParams, type FormValues } from "@/lib/iot/schema-form";
import { SchemaForm } from "@/components/shared/SchemaForm";
import { PointAddressEditor } from "@/components/iot/PointAddressEditor";

let pointSeq = 0;
const blankPoint = (driver: DriverInfo): FormPoint => ({
  key: `p${++pointSeq}`,
  metric: "",
  address: formToParams(driver.address_schema, formDefaults(driver.address_schema)),
  data_type: driver.data_types?.includes("int16") ? "int16" : (driver.data_types?.[0] ?? "int16"),
});

const EMPTY_ERRORS: FormFieldErrors = { link: {}, points: {}, other: [] };

export interface InstrumentFormV2Props {
  gatewayId: string;
  /** Drivers a new device on this gateway may use (`available`). */
  drivers: DriverInfo[];
  /** Serial ports from the gateway's birth. */
  serialPorts?: string[];
  templates: EquipmentTemplate[];
  onCancel: () => void;
  onCreated: (res: CreatedInstrument) => void;
}

export function InstrumentFormV2({
  gatewayId,
  drivers,
  serialPorts,
  templates,
  onCancel,
  onCreated,
}: InstrumentFormV2Props) {
  const nameId = useId();
  const pollId = useId();
  const driverId = useId();
  const tplId = useId();
  const [driver, setDriver] = useState<DriverInfo>(drivers[0]);
  const [name, setName] = useState("");
  const [pollSec, setPollSec] = useState("30");
  const [equipmentType, setEquipmentType] = useState("");
  const [linkValues, setLinkValues] = useState<FormValues>(() => formDefaults(drivers[0]?.link_schema));
  const [points, setPoints] = useState<FormPoint[]>(() => (drivers[0] ? [blankPoint(drivers[0])] : []));
  const [serverErrors, setServerErrors] = useState<FormFieldErrors>(EMPTY_ERRORS);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const link = useMemo(() => formToParams(driver.link_schema, linkValues), [driver, linkValues]);
  const linkCheck = useMemo(() => validateParams(driver.link_schema, link), [driver, link]);
  const namedPoints = points.filter((p) => p.metric.trim());
  const pointsValid = namedPoints.every((p) => validateParams(driver.address_schema, p.address).valid);
  const poll = Number(pollSec);
  const pollOk = pollSec.trim() === "" || isPollSec(poll);
  const canSubmit = !!name.trim() && linkCheck.valid && namedPoints.length > 0 && pointsValid && pollOk && !saving;
  const hasSerial = !!(driver.link_schema as { properties?: Record<string, unknown> }).properties?.serial_port;

  const pickDriver = (id: string) => {
    const d = drivers.find((x) => x.id === id);
    if (!d || d.id === driver.id) return;
    setDriver(d);
    setLinkValues(formDefaults(d.link_schema));
    setPoints([blankPoint(d)]);
    setEquipmentType("");
    setServerErrors(EMPTY_ERRORS);
  };

  const applyTemplate = (t: EquipmentTemplate | undefined) => {
    if (!t) {
      setEquipmentType("");
      return;
    }
    setEquipmentType(t.equipment_type);
    if (!name.trim()) setName(t.display_name.split(" — ")[0]);
    if (t.default_poll_sec) setPollSec(String(t.default_poll_sec));
    const pts = templateToFormPoints(t.params);
    if (pts.length > 0) setPoints(pts);
    if (t.slave_id) setLinkValues((v) => ({ ...v, unit_id: String(t.slave_id) }));
  };

  const patchPoint = (key: string, patch: Partial<FormPoint>) =>
    setPoints((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    setServerErrors(EMPTY_ERRORS);
    try {
      const res = await things.createInstrumentV2(gatewayId, {
        name: name.trim(),
        ...(equipmentType ? { equipment_type: equipmentType } : {}),
        ...(pollSec.trim() ? { poll_sec: poll } : {}),
        link: { driver: driver.id, params: link },
        params: formPointsToV2(points),
      });
      onCreated(res);
    } catch (e) {
      // params[i] is the i-th named point sent; map it back onto the form's rows.
      const f = toFormFieldErrors(fieldProblemsOf(e));
      const byKey: FormFieldErrors["points"] = {};
      for (const [i, fields] of Object.entries(f.points)) {
        const p = namedPoints[Number(i)];
        if (p) byKey[points.indexOf(p)] = fields;
      }
      setServerErrors({ ...f, points: byKey });
      setError(writeErrorMessage(e, "Couldn't add the instrument."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-elevated p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-on-surface">New instrument</span>
        <Button size="sm" variant="ghost" onClick={onCancel} aria-label="Cancel">
          <X className="size-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field id={driverId} label="Protocol">
          <NativeSelect id={driverId} value={driver.id} onChange={(e) => pickDriver(e.target.value)}>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {driverLabel(d.id, drivers)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        {isModbusDriver(driver.id) && templates.length > 0 ? (
          <Field id={tplId} label="Model template (optional)">
            <NativeSelect
              id={tplId}
              value={equipmentType}
              onChange={(e) => applyTemplate(templates.find((t) => t.equipment_type === e.target.value))}
            >
              <option value="">Custom (blank)…</option>
              {templates.map((t) => (
                <option key={t.equipment_type} value={t.equipment_type}>
                  {t.display_name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        ) : (
          <span />
        )}
        <Field id={nameId} label="Name">
          <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Press 3 PLC" />
        </Field>
        <Field id={pollId} label="Poll interval (s)">
          <Input
            id={pollId}
            type="number"
            min={1}
            value={pollSec}
            onChange={(e) => setPollSec(e.target.value)}
            aria-invalid={!pollOk}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="text-[11px] font-medium text-on-surface-muted">Connection</div>
        <SchemaForm
          schema={driver.link_schema}
          values={linkValues}
          onChange={setLinkValues}
          errors={{ ...linkCheck.errors, ...serverErrors.link }}
          choices={hasSerial && serialPorts ? { serial_port: serialPorts } : undefined}
          advanced={linkAdvancedFields(driver.id)}
          requiredFirst
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-[11px] font-medium text-on-surface-muted">Points</div>
        {points.map((p, i) => (
          <div key={p.key} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Input
                className="h-8 flex-1 font-mono text-xs"
                value={p.metric}
                onChange={(e) => patchPoint(p.key, { metric: e.target.value })}
                placeholder="metric, e.g. bath_temp"
                aria-label={`Point ${i + 1} metric`}
              />
              <label className="flex items-center gap-1 text-[10px] text-on-surface-muted" title="Cycle counter (the poll trigger)">
                <input
                  type="checkbox"
                  checked={!!p.is_cycle_counter}
                  onChange={(e) => patchPoint(p.key, { is_cycle_counter: e.target.checked })}
                  aria-label={`Point ${i + 1} is cycle counter`}
                />
                cyc
              </label>
              <Button
                size="sm"
                variant="ghost"
                className="text-danger hover:bg-danger/10"
                onClick={() => setPoints((ps) => ps.filter((x) => x.key !== p.key))}
                aria-label={`Remove point ${i + 1}`}
              >
                <X className="size-3.5" />
              </Button>
            </div>
            <PointAddressEditor
              driver={driver}
              address={p.address}
              dataType={p.data_type}
              ariaPrefix={`Point ${i + 1}`}
              serverErrors={serverErrors.points[i]}
              onChange={({ address, dataType }) => patchPoint(p.key, { address, data_type: dataType })}
            />
            {serverErrors.points[i]?.[""] && (
              <p className="text-[11px] text-danger">{serverErrors.points[i][""]}</p>
            )}
          </div>
        ))}
        <Button size="sm" variant="ghost" className="self-start" onClick={() => setPoints((ps) => [...ps, blankPoint(driver)])}>
          <Plus className="mr-1 size-3" /> Add point
        </Button>
      </div>

      {(error || serverErrors.other.length > 0) && (
        <div role="alert" className="rounded-md border border-danger/40 bg-danger/5 p-2 text-[11px] text-danger">
          {error}
          {serverErrors.other.map((o) => (
            <div key={o}>{o}</div>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void submit()} disabled={!canSubmit}>
          {saving ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : <Plus className="mr-1 size-3.5" />}
          Add instrument
        </Button>
      </div>
    </div>
  );
}

export interface InstrumentLinkEditorProps {
  gatewayId: string;
  /** The instrument as `GET /v1/things/{id}` returns it (link, secrets_set, driver). */
  instrument: Thing;
  driver: DriverInfo | undefined;
  serialPorts?: string[];
  onCancel: () => void;
  onSaved: (res: InstrumentPatched) => void;
}

export function InstrumentLinkEditor({
  gatewayId,
  instrument,
  driver,
  serialPorts,
  onCancel,
  onSaved,
}: InstrumentLinkEditorProps) {
  const nameId = useId();
  const pollId = useId();
  const pollHintId = useId();
  const secretsSet = instrument.secrets_set ?? [];
  const [name, setName] = useState(instrument.name);
  const [pollSec, setPollSec] = useState("");
  const [linkValues, setLinkValues] = useState<FormValues>(() =>
    paramsToForm(driver?.link_schema, instrument.link?.params ?? {}),
  );
  const [clearSecrets, setClearSecrets] = useState<Set<string>>(new Set());
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!driver || !instrument.link) {
    return (
      <div className="rounded-md border border-border bg-surface-elevated p-3 text-[11px] text-on-surface-muted">
        This instrument has no multi-protocol connection to edit.{" "}
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }

  const link = formToParams(driver.link_schema, linkValues, { clearSecrets });
  const linkCheck = validateParams(driver.link_schema, link, {
    secretsSet: secretsSet.filter((s) => !clearSecrets.has(s)),
  });
  const poll = Number(pollSec);
  const pollOk = pollSec.trim() === "" || isPollSec(poll);
  const canSave = !!name.trim() && linkCheck.valid && pollOk && !saving;
  const hasSerial = !!(driver.link_schema as { properties?: Record<string, unknown> }).properties?.serial_port;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setServerErrors({});
    try {
      const res = await things.patchInstrument(gatewayId, instrument.id, {
        ...(name.trim() !== instrument.name ? { name: name.trim() } : {}),
        ...(pollSec.trim() ? { poll_sec: poll } : {}),
        // No `params`: the points, their tags, bindings and history stay as they are.
        link: { driver: driver.id, params: link },
      });
      onSaved(res);
    } catch (e) {
      setServerErrors(toFormFieldErrors(fieldProblemsOf(e)).link);
      setError(writeErrorMessage(e, "Couldn't save the connection."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-brand/40 bg-surface-elevated p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-on-surface">
          Connection — {instrument.name} · {driverLabel(driver.id)}
        </span>
        <Button size="sm" variant="ghost" onClick={onCancel} aria-label="Cancel">
          <X className="size-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field id={nameId} label="Name">
          <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field id={pollId} label="Poll interval, every point (s)">
          <Input
            id={pollId}
            type="number"
            min={1}
            value={pollSec}
            onChange={(e) => setPollSec(e.target.value)}
            placeholder="unchanged"
            aria-invalid={!pollOk}
            aria-describedby={pollHintId}
          />
        </Field>
      </div>
      <p id={pollHintId} className="-mt-1 text-[11px] text-on-surface-muted">
        Sets every point&apos;s poll interval, replacing each point&apos;s own. Leave it empty to keep them; to change
        one point, edit it in the instrument&apos;s live values.
      </p>
      <SchemaForm
        schema={driver.link_schema}
        values={linkValues}
        onChange={setLinkValues}
        errors={{ ...linkCheck.errors, ...serverErrors }}
        secretsSet={secretsSet}
        clearSecrets={clearSecrets}
        onClearSecretsChange={setClearSecrets}
        choices={hasSerial && serialPorts ? { serial_port: serialPorts } : undefined}
        advanced={linkAdvancedFields(driver.id)}
        requiredFirst
      />
      <p className="text-[11px] text-on-surface-muted">
        Saving changes the connection (and every point&apos;s poll interval, when one is typed); the points and
        their history stay. The gateway gets the new config straight away.
      </p>
      {error && (
        <div role="alert" className="rounded-md border border-danger/40 bg-danger/5 p-2 text-[11px] text-danger">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void save()} disabled={!canSave}>
          {saving && <Loader2 className="mr-1 size-3.5 animate-spin" />}
          Save connection
        </Button>
      </div>
    </div>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 text-[11px] text-on-surface-muted">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}
